import React, { useState, useEffect } from 'react';
import './EditRequestModal.css';

const EditRequestModal = ({ isOpen, onClose, workItem, onSubmit, tiposCliente }) => {
  const [selectedServicos, setSelectedServicos] = useState([]);
  const [customActivities, setCustomActivities] = useState([]);
  const [reembolsos, setReembolsos] = useState([]);
  const [newActivity, setNewActivity] = useState({ nome: '', valor: '' });
  const [newReembolso, setNewReembolso] = useState({ descricao: '', valor: '' });
  const [availableServicos, setAvailableServicos] = useState([]);

  // Load available services from tipo cliente
  useEffect(() => {
    console.log('EditRequestModal - Debug:', {
      isOpen,
      hasWorkItem: !!workItem,
      workItem,
      tipoClienteId: workItem?.tipoClienteId,
      tipoClienteData: workItem?.tipoClienteData,
      tiposCliente,
      hasTiposCliente: !!tiposCliente,
      tiposClienteLength: tiposCliente?.length
    });
    
    if (isOpen && workItem && tiposCliente && tiposCliente.length > 0) {
      // Find the tipo cliente data - check multiple possible locations
      let tipoClienteId = null;
      
      if (workItem.tipoClienteId) {
        tipoClienteId = typeof workItem.tipoClienteId === 'string' 
          ? workItem.tipoClienteId 
          : workItem.tipoClienteId._id;
      } else if (workItem.tipoClienteData?._id) {
        tipoClienteId = workItem.tipoClienteData._id;
      }
      
      console.log('Looking for tipoClienteId:', tipoClienteId);
      
      if (tipoClienteId) {
        const tipoCliente = tiposCliente.find(tc => tc._id === tipoClienteId);
        console.log('Found tipoCliente:', tipoCliente);
        
        if (tipoCliente?.servicos && Array.isArray(tipoCliente.servicos)) {
          // Filter out services that are already in the work item
          const existingServicoNames = workItem.selectedServicos?.map(s => s.nome) || [];
          console.log('Existing service names:', existingServicoNames);
          
          const filteredServicos = tipoCliente.servicos.filter(
            servico => !existingServicoNames.includes(servico.nome)
          );
          console.log('Filtered services:', filteredServicos);
          setAvailableServicos(filteredServicos);
        } else {
          console.log('No services in tipoCliente');
          setAvailableServicos([]);
        }
      } else {
        console.log('No tipoClienteId found');
        setAvailableServicos([]);
      }
    } else {
      setAvailableServicos([]);
    }
  }, [isOpen, workItem, tiposCliente]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedServicos([]);
      setCustomActivities([]);
      setReembolsos([]);
      setNewActivity({ nome: '', valor: '' });
      setNewReembolso({ descricao: '', valor: '' });
    }
  }, [isOpen]);

  const handleServicoToggle = (servico) => {
    setSelectedServicos(prev => {
      const isSelected = prev.some(s => s.nome === servico.nome);
      if (isSelected) {
        return prev.filter(s => s.nome !== servico.nome);
      } else {
        return [...prev, { ...servico, type: 'servico' }];
      }
    });
  };

  const handleAddActivity = () => {
    if (newActivity.nome && newActivity.valor) {
      const activity = {
        id: Date.now().toString(),
        nome: newActivity.nome,
        valor: parseFloat(newActivity.valor),
        type: 'activity'
      };
      setCustomActivities([...customActivities, activity]);
      setNewActivity({ nome: '', valor: '' });
    }
  };

  const handleRemoveActivity = (id) => {
    setCustomActivities(customActivities.filter(a => a.id !== id));
  };

  const handleAddReembolso = () => {
    if (newReembolso.descricao && newReembolso.valor) {
      const reembolso = {
        id: Date.now().toString(),
        nome: newReembolso.descricao, // Using 'nome' for consistency in submission
        descricao: newReembolso.descricao,
        valor: parseFloat(newReembolso.valor),
        type: 'reembolso'
      };
      setReembolsos([...reembolsos, reembolso]);
      setNewReembolso({ descricao: '', valor: '' });
    }
  };

  const handleRemoveReembolso = (id) => {
    setReembolsos(reembolsos.filter(r => r.id !== id));
  };

  const handleSubmit = () => {
    const allItems = [...selectedServicos, ...customActivities, ...reembolsos];
    if (allItems.length > 0) {
      onSubmit(allItems);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const calculateTotal = () => {
    const servicosTotal = selectedServicos.reduce((sum, s) => sum + (s.valor || 0), 0);
    const activitiesTotal = customActivities.reduce((sum, a) => sum + (a.valor || 0), 0);
    const reembolsosTotal = reembolsos.reduce((sum, r) => sum + (r.valor || 0), 0);
    return servicosTotal + activitiesTotal + reembolsosTotal;
  };

  const totalValue = calculateTotal();
  const hasItems = selectedServicos.length > 0 || customActivities.length > 0 || reembolsos.length > 0;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-request-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Solicitar Edição do Item Concluído</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="details-section">
            <div className="edit-request-warning">
              <div className="warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div className="warning-content">
                <h4>Item Concluído e Bloqueado</h4>
                <p>Este item está concluído e bloqueado para edição. Adicione os itens que deseja incluir e envie para aprovação do cliente.</p>
              </div>
            </div>
          </div>

          {availableServicos.length > 0 && (
            <div className="details-section">
              <h3>Serviços do Tipo de Cliente</h3>
              <div className="servicos-list">
                {availableServicos.map((servico, index) => {
                  const isSelected = selectedServicos.some(s => s.nome === servico.nome);
                  return (
                    <label key={index} className="servico-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleServicoToggle(servico)}
                      />
                      <span className="servico-info">
                        <span>{servico.nome}</span>
                        <span className="servico-valor">{formatCurrency(servico.valor)}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="details-section">
            <h3>Atividades Adicionais</h3>
            <div className="add-item-section">
              <div className="add-item-form">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nome da atividade"
                  value={newActivity.nome}
                  onChange={(e) => setNewActivity({ ...newActivity, nome: e.target.value })}
                />
                <input
                  type="number"
                  className="form-control"
                  placeholder="Valor"
                  value={newActivity.valor}
                  onChange={(e) => setNewActivity({ ...newActivity, valor: e.target.value })}
                  step="0.01"
                  min="0"
                />
                <button type="button" className="btn-add" onClick={handleAddActivity}>
                  Adicionar
                </button>
              </div>

              {customActivities.length > 0 && (
                <div className="items-list">
                  {customActivities.map(activity => (
                    <div key={activity.id} className="item-row">
                      <span>{activity.nome}</span>
                      <span>{formatCurrency(activity.valor)}</span>
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => handleRemoveActivity(activity.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="details-section">
            <h3>Reembolsos</h3>
            <div className="add-item-section">
              <div className="add-item-form">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Descrição do reembolso"
                  value={newReembolso.descricao}
                  onChange={(e) => setNewReembolso({ ...newReembolso, descricao: e.target.value })}
                />
                <input
                  type="number"
                  className="form-control"
                  placeholder="Valor"
                  value={newReembolso.valor}
                  onChange={(e) => setNewReembolso({ ...newReembolso, valor: e.target.value })}
                  step="0.01"
                  min="0"
                />
                <button type="button" className="btn-add" onClick={handleAddReembolso}>
                  Adicionar
                </button>
              </div>

              {reembolsos.length > 0 && (
                <div className="items-list">
                  {reembolsos.map(reembolso => (
                    <div key={reembolso.id} className="item-row">
                      <span>{reembolso.descricao}</span>
                      <span>{formatCurrency(reembolso.valor)}</span>
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => handleRemoveReembolso(reembolso.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {hasItems && (
            <div className="details-section">
              <div className="totals-details">
                <div className="total-row">
                  <h3>Total dos Novos Itens</h3>
                  <span className="total-value previsto">{formatCurrency(totalValue)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="details-actions">
            <button className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={!hasItems}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
              </svg>
              Enviar Solicitação
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRequestModal;
